import { format } from 'date-fns';

export interface JournalWebhookResponse {
  currentDay: {
    motivation: string;
  };
  nextDay: {
    tasks: Array<{
      id: string;
      text: string;
      completed: boolean;
    }>;
  };
}

export interface DietPlanResponse {
  Monday?: DayPlan;
  Tuesday?: DayPlan;
  Wednesday?: DayPlan;
  Thursday?: DayPlan;
  Friday?: DayPlan;
  Saturday?: DayPlan;
  Sunday?: DayPlan;
}

export interface DayPlan {
  plan: {
    meals: Array<{
      time: string;
      name: string;
      foods: string[];
      calories: number;
      protein: number;
      carbs: number;
      fats: number;
    }>;
    recommendations: string[];
    supplements: string[];
  };
}

export async function sendJournalDataToWebhook(journalData: any): Promise<JournalWebhookResponse> {
  try {
    const response = await fetch('https://hook.eu2.make.com/s6bksvy1qha2v4qhpyuwymptot6hiuk6', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'journal',
        ...journalData,
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let responseText = await response.text();
    
    try {
      // Handle chunked or incomplete responses
      if (responseText.includes('```json')) {
        // Extract JSON from code blocks
        const matches = responseText.match(/```json\s*([\s\S]*?)\s*```/);
        if (matches && matches[1]) {
          responseText = matches[1].trim();
        }
      }

      // Try to fix common JSON issues
      responseText = responseText
        .replace(/\n/g, ' ')           // Replace newlines with spaces
        .replace(/\s+/g, ' ')          // Normalize whitespace
        .replace(/,\s*([}\]])/g, '$1') // Remove trailing commas
        .replace(/([{[,:])\s*}/g, '$1}') // Fix empty objects
        .trim();

      const parsedResponse = JSON.parse(responseText);

      // Validate journal response structure
      if (!parsedResponse.currentDay?.motivation) {
        throw new Error('Invalid journal response: missing motivation');
      }

      if (!Array.isArray(parsedResponse.nextDay?.tasks)) {
        throw new Error('Invalid journal response: missing or invalid tasks array');
      }

      // Ensure all tasks have required fields
      parsedResponse.nextDay.tasks = parsedResponse.nextDay.tasks.map(task => ({
        id: task.id || `task-${crypto.randomUUID()}`,
        text: task.text || '',
        completed: Boolean(task.completed)
      }));

      return parsedResponse;
    } catch (e) {
      console.error('Failed to parse webhook response:', e);
      console.error('Raw response:', responseText);
      throw new Error('Invalid response format from webhook');
    }
  } catch (error: any) {
    console.error('Error sending data to webhook:', error);
    throw new Error(error.message || 'Failed to send data to webhook');
  }
}

export async function sendDietPlanWebhook(profileData: any): Promise<DietPlanResponse> {
  try {
    const response = await fetch('https://hook.eu2.make.com/c43ecffbes2onjvn2ex7hoyqfth6in9f', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        type: 'diet_plan',
        ...profileData,
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    let responseText = await response.text();
    
    try {
      // Handle chunked or incomplete responses
      if (responseText.includes('```json')) {
        // Extract JSON from code blocks
        const matches = responseText.match(/```json\s*([\s\S]*?)\s*```/);
        if (matches && matches[1]) {
          responseText = matches[1].trim();
        }
      }

      // Fix time format issues (e.g., "08":00" -> "08:00")
      responseText = responseText.replace(/"(\d{2})":(\d{2})"/g, '"$1:$2"');

      // Try to fix common JSON issues
      responseText = responseText
        .replace(/\n/g, ' ')           // Replace newlines with spaces
        .replace(/\s+/g, ' ')          // Normalize whitespace
        .replace(/,\s*([}\]])/g, '$1') // Remove trailing commas
        .replace(/([{[,:])\s*}/g, '$1}') // Fix empty objects
        .replace(/\\/g, '')            // Remove escape characters
        .replace(/([a-zA-Z0-9_]+):/g, '"$1":') // Quote unquoted keys
        .replace(/:\s*'([^']*?)'/g, ':"$1"')   // Replace single quotes with double quotes
        .replace(/,\s*([}\]])/g, '$1')         // Remove trailing commas
        .replace(/}\s*{/g, '},{')              // Fix object separation
        .trim();

      // Try to parse the response
      let parsedResponse: DietPlanResponse;
      try {
        parsedResponse = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Initial parse failed:', parseError);
        console.error('Attempting to fix malformed JSON...');
        
        // Additional fixes for malformed JSON
        responseText = responseText
          .replace(/([^\\])"([^"]*?)\\?"([^"]*?)([^\\])"/g, '$1"$2$3$4"') // Fix escaped quotes
          .replace(/([^":\s])"/g, '$1\\"')  // Escape unescaped quotes
          .replace(/"([^"]*?)""/g, '"$1"')  // Fix double quotes
          .replace(/,(\s*[}\]])/g, '$1');   // Remove trailing commas
        
        parsedResponse = JSON.parse(responseText);
      }

      // Validate and normalize each day's plan
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
      const normalizedResponse: DietPlanResponse = {};
      
      days.forEach(day => {
        if (parsedResponse[day]) {
          normalizedResponse[day] = {
            plan: {
              meals: (parsedResponse[day]?.plan?.meals || []).map(meal => ({
                time: String(meal.time || '').replace(/"/g, ''),  // Remove any remaining quotes
                name: String(meal.name || ''),
                foods: Array.isArray(meal.foods) ? meal.foods.map(String) : [],
                calories: Number(meal.calories) || 0,
                protein: Number(meal.protein) || 0,
                carbs: Number(meal.carbs) || 0,
                fats: Number(meal.fats) || 0
              })),
              recommendations: Array.isArray(parsedResponse[day]?.plan?.recommendations)
                ? parsedResponse[day]!.plan.recommendations.map(String)
                : [],
              supplements: Array.isArray(parsedResponse[day]?.plan?.supplements)
                ? parsedResponse[day]!.plan.supplements.map(String)
                : []
            }
          };
        }
      });

      // Validate that we have at least one day's plan
      if (Object.keys(normalizedResponse).length === 0) {
        throw new Error('No valid diet plans found in response');
      }

      return normalizedResponse;
    } catch (e) {
      console.error('Failed to parse webhook response:', e);
      console.error('Raw response:', responseText);
      throw new Error('Failed to parse webhook response');
    }
  } catch (error: any) {
    console.error('Error in webhook request:', error);
    throw error;
  }
}